import React from 'react';

import { Checkbox, createStyles, Paper, Tabs, Tooltip, WithStyles, withStyles } from '@material-ui/core';
import { ArrowDropDown as ArrowDropDownIcon, Lock as LockIcon, LockOpen as LockOpenIcon } from '@material-ui/icons';
import classnames from 'classnames';
import { LumaTheme } from 'embeds/core/styles/getTheme';
import { includes, map, startCase, upperCase } from 'lodash';

import { FiltersBadge, Tab } from 'components/v2';

import { useFiltersContext, useTabContext } from './context/FixedFiltersContext';
import FilterAppointmentType from './FilterAppointmentType';
import FilterDiagnoses from './FilterDiagnoses';
import FilterFacility from './FilterFacility';
import FilterProcedures from './FilterProcedures';
import FilterProvider from './FilterProvider';
import FiltersAppointmentDate from './FiltersAppointmentDate';
import FiltersAppointmentStatus from './FiltersAppointmentStatus';
import FiltersBillingAmount from './FiltersBillingAmount';
import FiltersBillingDate from './FiltersBillingDate';
import FiltersBillingItem from './FiltersBillingItem';
import FiltersBillingStatus from './FiltersBillingStatus';

const styles = ({
  custom: {
    fontSizes,
    palette: { colorSupportMidnight, colorSupportPowder, colorSupportSteel },
  },
  palette: {
    common: { white },
  },
  spacing: { unit },
}: LumaTheme) =>
  createStyles({
    tabsWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      borderTop: `1px solid  ${colorSupportMidnight}`,
      background: `${colorSupportMidnight}`,
      maxHeight: 33,
    },
    indicatorNone: {
      backgroundColor: 'transparent',
    },
    tab: {
      height: 32,
      maxHeight: 32,
      fontSize: fontSizes.body,
      fontWeight: 400,
      minHeight: 32,
      borderRight: `1px solid ${colorSupportPowder}`,
    },
    tabHeaderElement: {
      paddingRight: unit * 1.5,
    },
    tabIcon: {
      fontSize: 30,
    },
    tabApptDateIcon: {
      fontSize: fontSizes.subHeading,
    },
    tabs: {
      maxHeight: 32,
      minHeight: 32,
    },
    tabContent: {
      background: white,
    },
    lockIcon: {
      color: `${colorSupportPowder}`,
    },
    tooltip: {
      padding: '4px 6px',
      backgroundColor: colorSupportSteel,
      fontSize: fontSizes.tiny,
      fontWeight: 500,
      lineHeight: 1.2,
    },
    readOnly: {
      cursor: 'not-allowed',
    },
  });

interface FixedFilterProps extends WithStyles<typeof styles> {
  locked?: boolean;
}

const FixedFilters: React.FC<FixedFilterProps> = ({ classes }) => {
  const { tabs, selectedTab, changeTab, isFilterLocked, toggleFilterLocked } = useTabContext();
  const { filters, count, readOnly, hideLockButton } = useFiltersContext();

  const FilterIcon: React.FC<{ count: number }> = ({ count: badgeCount }) => (
    <React.Fragment>
      <FiltersBadge badge={badgeCount} />
      <ArrowDropDownIcon className={classnames(classes.tabHeaderElement, classes.tabIcon)} />
    </React.Fragment>
  );

  const badgeCount = (tab: string) => {
    // WORKAROUND there's some race condition/rerender that makes `filters[tab].length` fail
    if (!filters[tab]) {
      return '';
    }
    if (includes(filters[tab], 'all') && filters[tab].length === 1) return 'All';
    if (includes(filters[tab], 'all') && filters[tab].length > 1) return count[tab] - filters[tab].length + 1;
    return filters[tab].length;
  };

  const getTabName = (tab: string) => {
    const firstLetters = tab.substr(0, 3);
    const restOfString = tab.substr(3, tab.length - 3);

    switch (tab) {
      case 'billingDateRequested':
        return 'Date Requested';
      case 'billingAmount':
        return 'Amount';
      case 'billingItem':
        return 'Billing Item';
      case 'billingDatePaid':
        return 'Date Paid';
      case 'billingStatus':
        return 'Status';
      default:
        return firstLetters === 'cpt' || firstLetters === 'icd'
          ? `${upperCase(firstLetters)} ${startCase(restOfString)}`
          : startCase(tab);
    }
  };

  const getTabIcon = (tab: string) => {
    const showCountBadge = !/apptDate|billingAmount|billingDate/.test(tab);

    if (showCountBadge) {
      return <FilterIcon count={badgeCount(tab)} />;
    }
    return <ArrowDropDownIcon className={classes.tabApptDateIcon} />;
  };

  return (
    <>
      <Paper className={classes.tabsWrapper} elevation={0} square>
        <Tabs value={selectedTab.index} classes={{ indicator: classes.indicatorNone, root: classes.tabs }}>
          {map(tabs, (tab, index) => (
            <Tab
              backgroundColor="supportMidnight"
              className={classes.tab}
              color="white"
              fontSize="normal"
              icon={getTabIcon(tab)}
              invertSelectedColors
              key={tab}
              label={<span className={classes.tabHeaderElement}>{getTabName(tab)}</span>}
              onClick={() => changeTab({ tab, index })}
            />
          ))}
        </Tabs>
        {!readOnly && !hideLockButton && (
          <Tooltip
            title={isFilterLocked ? 'Filters saved' : 'Save filters'}
            placement="top"
            classes={{ tooltip: classes.tooltip }}
          >
            <Checkbox
              checked={isFilterLocked}
              checkedIcon={<LockIcon className={classes.lockIcon} />}
              icon={<LockOpenIcon className={classes.lockIcon} />}
              onChange={toggleFilterLocked}
            />
          </Tooltip>
        )}
      </Paper>
      <Paper className={classnames(classes.tabContent, { [classes.readOnly]: readOnly })} elevation={0} square>
        {selectedTab.tab === 'apptDate' && <FiltersAppointmentDate />}
        {selectedTab.tab === 'apptStatus' && <FiltersAppointmentStatus />}
        {selectedTab.tab === 'apptType' && <FilterAppointmentType />}
        {selectedTab.tab === 'billingItem' && <FiltersBillingItem />}
        {selectedTab.tab === 'billingAmount' && <FiltersBillingAmount />}
        {selectedTab.tab === 'billingDatePaid' && <FiltersBillingDate filterName="billingDatePaid" />}
        {selectedTab.tab === 'billingDateRequested' && <FiltersBillingDate filterName="billingDateRequested" />}
        {selectedTab.tab === 'billingStatus' && <FiltersBillingStatus />}
        {selectedTab.tab === 'cptCode' && <FilterProcedures />}
        {selectedTab.tab === 'facility' && <FilterFacility />}
        {selectedTab.tab === 'icd10Code' && <FilterDiagnoses key="icd10" type="icd10" />}
        {selectedTab.tab === 'icd9Code' && <FilterDiagnoses key="icd9" type="icd9" />}
        {selectedTab.tab === 'provider' && <FilterProvider type="default" />}
      </Paper>
    </>
  );
};

export default withStyles(styles)(FixedFilters);
